import { useEffect, useState, useCallback } from 'react';
// @ts-expect-error - lodash.debounce doesn't have type definitions
import debounce from 'lodash.debounce';

import { Box, TextField, Autocomplete, CircularProgress } from '@mui/material';
import { getActiveManifests } from '@/actions/manifest';
import { useApi } from '@/providers/ApiContext';
import type { Manifest } from '@/types/apiTypes';
import type { EntityId } from '@/types/apiTypes';

const MAX_ITEMS = 10;

interface InspectionManifestsFieldProps {
  document_type_id: EntityId | undefined;
  value: Manifest[];
  setValue: (value: Manifest[]) => void;
  setChanged: () => void;
  edit: boolean;
}

function InspectionManifestsField({
  document_type_id,
  value,
  setValue,
  setChanged,
  edit,
}: InspectionManifestsFieldProps): React.JSX.Element {
  const api = useApi();
  const [query, setQuery] = useState<string>('');
  const [manifests, setManifests] = useState<Manifest[] | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [selected, setSelected] = useState<Manifest[]>(value);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [cursors, setCursors] = useState<Record<number, string>>({});
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const fetchManifests = useCallback(
    (newQuery: string, startKey: string | null = null) => {
      debounce(async () => {
        if (!document_type_id || !api) return;
        setLoading(true);
        try {
          const response = await getActiveManifests(
            api,
            document_type_id,
            20,
            startKey || undefined,
            Boolean(newQuery) ? newQuery : undefined,
          );
          setManifests((prevOptions) =>
            startKey === null
              ? response.results
              : [...(prevOptions || []), ...response.results],
          );
          setHasNextPage(response.last_key !== null);
          if (response.last_key) {
            setCursors((prev) => ({
              ...prev,
              [Object.keys(prev).length + 1]: response.last_key!,
            }));
          }
        } catch (error) {
          console.log(error);
          setManifests([]);
        } finally {
          setLoading(false);
        }
      }, 300)();
    },
    [api, document_type_id],
  );

  useEffect(() => {
    if (isOpen && !manifests) {
      setCursors({});
      fetchManifests(''); // Fetch initial items when dropdown opens
    }
  }, [isOpen, manifests, fetchManifests]);

  useEffect(() => {
    if (query.length >= 1) {
      setCursors({});
      fetchManifests(query); // Fetch new items for new query
    } else if (query.length === 0 && isOpen) {
      setCursors({});
      fetchManifests(''); // Reset to initial items when query is cleared
    }
  }, [query, fetchManifests, isOpen]);

  const handleScroll = (event: React.UIEvent<HTMLUListElement>) => {
    const listboxNode = event.currentTarget;
    if (
      listboxNode.scrollHeight - listboxNode.scrollTop ===
        listboxNode.clientHeight &&
      !loading &&
      hasNextPage
    ) {
      const nextCursor = cursors[Object.keys(cursors).length];
      fetchManifests(query, nextCursor);
    }
  };

  const handleChange = (
    _event: React.SyntheticEvent,
    newValues: Manifest[],
  ) => {
    setChanged();
    if (newValues.length <= MAX_ITEMS) {
      setSelected(newValues);
      setValue(newValues);
    }
  };

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '600px',
        justifyContent: 'center',
        justifyItems: 'center',
        textAlign: 'justify',
      }}
    >
      <Autocomplete
        id='manifests-field'
        multiple
        options={manifests || []}
        loading={loading}
        sx={{ width: '100%' }}
        onInputChange={(_event, newInputValue) => setQuery(newInputValue)}
        onOpen={() => setIsOpen(true)}
        onClose={() => {
          setIsOpen(false);
          setManifests(null);
        }}
        ListboxProps={{
          onScroll: handleScroll,
        }}
        value={selected}
        onChange={handleChange}
        disabled={edit}
        getOptionLabel={(option) => (option ? `${option.name}` : '')}
        disableClearable={true}
        renderInput={(params) => (
          <TextField
            {...params}
            required
            label={`Select the Inspection Manifests`}
            placeholder={
              !selected.length
                ? 'Click to load manifest list or start typing...'
                : ''
            }
            helperText={
              selected.length === MAX_ITEMS
                ? `Manifest limit is ${MAX_ITEMS}.`
                : `Select the inspection manifest(s) for the document type you chose above. Manifests contain SOP-derived requirements for that document type. You may select multiple manifests, but incorrect choices can lead to irrelevant results. You can also type to search faster.`
            }
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress color='inherit' size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </Box>
  );
}

export default InspectionManifestsField;

